import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Building, Code } from 'lucide-react';
import { AnimatedSection } from '../ui/AnimatedSection';
import { experiences } from '../../data/portfolioData';

export const Experience: React.FC = () => {
  return (
    <section id="experience" className="py-20 bg-slate-100 dark:bg-slate-800 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-10 w-1.5 h-1.5 bg-indigo-400 rounded-full opacity-20 animate-float-dots" />
        <div className="absolute top-2/3 right-20 w-1 h-1 bg-primary-400 rounded-full opacity-15 animate-float-dots" 
             style={{ animationDelay: '-3s' }} />
        <div className="absolute bottom-1/3 left-1/3 w-2 h-2 bg-indigo-500 rounded-full opacity-10 animate-float-dots" 
             style={{ animationDelay: '-6s' }} />
        
        {/* Floating lines */}
        <div className="absolute top-1/2 left-0 h-px w-24 bg-gradient-to-r from-transparent via-indigo-400 to-transparent opacity-10 animate-float-lines" />
        <div className="absolute bottom-1/4 right-0 h-px w-32 bg-gradient-to-l from-transparent via-primary-400 to-transparent opacity-8 animate-float-lines" 
             style={{ animationDelay: '-4s' }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <AnimatedSection>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-slate-100 mb-4">
              Expérience Professionnelle
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-500 to-primary-500 mx-auto"></div>
          </div>
        </AnimatedSection>

        <div className="relative">
          {/* Enhanced Timeline line */}
          <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 w-1 h-full bg-gradient-to-b from-indigo-500 via-primary-500 to-indigo-500 rounded-full shadow-lg"></div>
          
          <div className="space-y-12">
            {experiences.map((exp, index) => (
              <AnimatedSection key={exp.id} delay={index * 0.2}>
                <motion.div
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.6 }}
                  viewport={{ once: true }}
                  className={`relative flex items-center ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  
                  {/* Enhanced Content */}
                  <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:text-right md:pr-8' : 'md:pl-8'} ml-20 md:ml-0`}>
                    <motion.div
                      className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-slate-200 dark:border-slate-700 relative overflow-hidden group"
                      whileHover={{ scale: 1.02, y: -5 }}
                    >
                      {/* Subtle gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-primary-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      
                      <div className="relative z-10">
                        {/* Enhanced Period badge */}
                        <motion.div 
                          className="inline-flex items-center gap-2 mb-3 px-4 py-2 bg-gradient-to-r from-indigo-100 to-primary-100 dark:from-indigo-950/40 dark:to-primary-950/40 rounded-full border border-indigo-200 dark:border-indigo-800"
                          whileHover={{ scale: 1.05 }}
                        >
                          <Calendar size={14} className="text-indigo-600 dark:text-indigo-400" />
                          <span className="text-sm text-indigo-600 dark:text-indigo-400 font-medium">
                            {exp.period}
                          </span>
                        </motion.div>
                        
                        {/* Job title */}
                        <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-1 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                          {exp.title}
                        </h3>
                        
                        {/* Company */}
                        <motion.div 
                          className="flex items-center gap-2 mb-3"
                          whileHover={{ x: index % 2 === 0 ? -5 : 5 }}
                        >
                          <Building size={16} className="text-primary-600 dark:text-primary-400" />
                          <h4 className="text-lg text-primary-600 dark:text-primary-400 font-semibold">
                            {exp.company}
                          </h4>
                        </motion.div>
                        
                        {/* Description */}
                        <p className="text-slate-600 dark:text-slate-300 mb-4 leading-relaxed">
                          {exp.description}
                        </p>
                        
                        {/* Technologies */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 mb-2">
                            <motion.div
                              whileHover={{ rotate: 180 }}
                              transition={{ duration: 0.3 }}
                            >
                              <Code size={14} className="text-slate-500" />
                            </motion.div>
                            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
                              Technologies utilisées :
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {exp.technologies.map((tech, techIndex) => (
                              <motion.span
                                key={tech}
                                className="px-3 py-1 bg-indigo-100 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400 rounded-full text-sm font-medium border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-200 dark:hover:bg-indigo-900/40 transition-colors"
                                whileHover={{ scale: 1.1, y: -2 }}
                                whileTap={{ scale: 0.95 }}
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.1 + techIndex * 0.05 }}
                                viewport={{ once: true }}
                              >
                                {tech}
                              </motion.span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
          
        </div>
      </div>
    </section>
  );
};